// maskburn.ts — ingest-time PII mask burn-in (EGO-163). Thin browser seam over
// core/'s oracle-parity masker (core/src/maskBurn.ts + maskBurnVideo.ts):
// read left.mp4/right.mp4 out of a staged session dir, black out every
// user-defined 3D privacy region in every frame, and write the masked video
// back OVER the originals. Mirrors proxy.ts's shape — with one crucial
// difference: this is NOT best-effort. A masked session whose burn failed must
// never upload, so callers let this throw, and upload.ts refuses any session
// with meta.masks.count > 0 and no mask_burn.json.
//
// Ordering: burn BEFORE the review proxy, so the proxy is cut from masked video.
//
// Idempotence: mask_burn.json in the session dir is the done-marker. It is a
// bare (raw) path like masks.json — NOT in upload.ts's DERIVED_RE — because it
// describes what happened to the raw capture itself.
import { webSource } from "../../core/src/webSource.ts";
import { parseMasks, planEye, type Eye } from "../../core/src/maskBurn.ts";
import { burnEye, videoDimensions, type EyeBurnStats } from "../../core/src/maskBurnVideo.ts";

export const BURN_MARKER = "mask_burn.json";
const TOOL = "portal-maskburn";
const EYES: Eye[] = ["left", "right"];
/** Written while encoding, renamed over the original on success. */
const tmpName = (eye: Eye) => `${eye}.mp4.burning`;

async function fileExists(dir: FileSystemDirectoryHandle, name: string): Promise<boolean> {
  try { await dir.getFileHandle(name); return true; }
  catch { return false; }
}

async function readJson(dir: FileSystemDirectoryHandle, name: string): Promise<any> {
  return JSON.parse(await (await (await dir.getFileHandle(name)).getFile()).text());
}

async function writeFile(
  dir: FileSystemDirectoryHandle, name: string, data: Uint8Array | string,
): Promise<FileSystemFileHandle> {
  const fh = await dir.getFileHandle(name, { create: true });
  const ws = await fh.createWritable();
  await ws.write(data as any);
  await ws.close();
  return fh;
}

async function removeIfPresent(dir: FileSystemDirectoryHandle, name: string): Promise<void> {
  try { await dir.removeEntry(name); } catch { /* not there */ }
}

export interface MaskBurnResult {
  skipped: boolean;
  /** Why it was skipped — "already burned", "no masks", … */
  reason?: string;
  regions?: number;
  eyes?: Partial<Record<Eye, EyeBurnStats>>;
}

/** True when this session declares PII mask regions (schema-9+ meta.json). */
export function metaHasMasks(meta: any): boolean {
  return Number(meta?.masks?.count ?? 0) > 0;
}

/**
 * Burn `dir`'s masks.json regions into its left/right eye videos, in place.
 *
 * Skips (returns, never throws) when the session declares no masks or is already
 * burned. THROWS on every other failure — a masked session that cannot be masked
 * must not continue down the sync path.
 */
export async function maskBurnInto(
  dir: FileSystemDirectoryHandle,
  opts: { log?: (m: string) => void; onProgress?: (frac: number) => void } = {},
): Promise<MaskBurnResult> {
  const log = opts.log ?? (() => {});

  let meta: any;
  try { meta = await readJson(dir, "meta.json"); }
  catch { throw new Error("no parseable meta.json — cannot decide whether this session needs masking"); }

  const declared = metaHasMasks(meta);
  const hasMasksFile = await fileExists(dir, meta?.masks?.file ?? "masks.json");
  if (!declared && !hasMasksFile) {
    return { skipped: true, reason: "no masks" };
  }
  // Fail closed on a half-present mask record: a session that says it has PII
  // regions but ships no masks.json cannot be masked, and must not sneak past.
  if (declared && !hasMasksFile) {
    throw new Error(`meta.json declares ${meta.masks.count} PII mask region(s) but ${meta?.masks?.file ?? "masks.json"} is missing`);
  }
  if (await fileExists(dir, BURN_MARKER)) {
    log(`  ${BURN_MARKER} already present — masks already burned, skipping`);
    return { skipped: true, reason: "already burned" };
  }

  const masks = parseMasks(await (await (await dir.getFileHandle(meta?.masks?.file ?? "masks.json")).getFile()).text());
  const src = webSource(dir);
  const nRegions = masks.regions.length;
  log(`  burning ${nRegions} PII mask region(s) into the eye videos…`);

  // Residue from an interrupted earlier attempt would otherwise ride the upload
  // (upload.ts walks every file — there is no allowlist).
  for (const eye of EYES) await removeIfPresent(dir, tmpName(eye));

  const present: Eye[] = [];
  for (const eye of EYES) if (await fileExists(dir, `${eye}.mp4`)) present.push(eye);
  if (!present.length) throw new Error("session declares PII masks but has no left.mp4/right.mp4 to mask");

  const stats: Partial<Record<Eye, EyeBurnStats>> = {};
  try {
    for (let i = 0; i < present.length; i++) {
      const eye = present[i];
      const file = await (await dir.getFileHandle(`${eye}.mp4`)).getFile();
      // meta.json's capture size can disagree with the container; the rasterizer
      // must match the CANVAS, so the video wins (as it does in the oracle).
      const dim = await videoDimensions(file);
      const plan = await planEye(src, eye, meta, masks, { width: dim.w, height: dim.h });
      const { bytes, stats: st } = await burnEye(file, plan, {
        onProgress: (frac) => opts.onProgress?.((i + frac) / present.length),
      });
      if (st.frames !== st.framesExpected) {
        throw new Error(`${eye}: masked ${st.frames} frames but expected ${st.framesExpected} — refusing to replace the original`);
      }
      // Write beside the original first, then rename over it: a crash mid-write
      // must not leave a truncated (and silently unmasked-tail) eye video.
      const tmp = await writeFile(dir, tmpName(eye), bytes);
      const mv = (tmp as any).move;
      if (typeof mv === "function") {
        await mv.call(tmp, dir, `${eye}.mp4`);
      } else {
        // No FileSystemFileHandle.move() here — fall back to an overwrite. The
        // bytes are already fully in memory, so the window is a single write.
        await writeFile(dir, `${eye}.mp4`, bytes);
        await removeIfPresent(dir, tmpName(eye));
      }
      stats[eye] = st;
      log(`  ${eye}.mp4 masked: ${st.frames} frames, ${st.failedClosed} failed closed, ` +
          `mean ${st.meanPct.toFixed(2)}% / max ${st.maxPct.toFixed(2)}% of frame, ` +
          `${(st.outputBytes / 1048576).toFixed(1)} MB in ${st.seconds.toFixed(1)}s`);
    }
  } catch (e) {
    for (const eye of EYES) await removeIfPresent(dir, tmpName(eye));
    throw e;
  }

  const marker = {
    schema: 1,
    tool: TOOL,
    burned_utc: new Date().toISOString(),
    regions: nRegions,
    region_ids: masks.regions.map((r) => r.id),
    dilate_px: 8,
    eyes: Object.fromEntries(Object.entries(stats).map(([eye, st]) => [eye, {
      frames: st!.frames,
      failed_closed: st!.failedClosed,
      mean_pct: Number(st!.meanPct.toFixed(4)),
      max_pct: Number(st!.maxPct.toFixed(4)),
      width: st!.width,
      height: st!.height,
      // Bitrates in bps. source_bitrate is what the raw eye video spent;
      // target_bitrate is what the re-encode asked the hardware encoder for
      // (equal, by policy — see maskBurnVideo.ts). output_bytes/input_bytes are
      // the container sizes, so the burn's size delta is auditable from the marker.
      source_bitrate: st!.sourceBitrate,
      target_bitrate: st!.targetBitrate,
      input_bytes: st!.inputBytes,
      output_bytes: st!.outputBytes,
    }])),
  };
  await writeFile(dir, BURN_MARKER, JSON.stringify(marker, null, 2));
  opts.onProgress?.(1);
  return { skipped: false, regions: nRegions, eyes: stats };
}
